// Persistent Session Management Service
// StoredUser uses 'any' to allow compatibility with the User type from authSlice
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type StoredUser = Record<string, any>;

class PersistentSessionManager {
  private static instance: PersistentSessionManager;
  private refreshTimer: ReturnType<typeof setInterval> | null = null;
  private readonly REFRESH_INTERVAL = 7 * 24 * 60 * 60 * 1000; // 7 days - check weekly
  private readonly TOKEN_KEY = 'token';
  private readonly USER_KEY = 'user';
  private readonly REFRESH_TOKEN_KEY = 'refresh_token';

  private constructor() {
    this.initializeSession();
  }

  public static getInstance(): PersistentSessionManager {
    if (!PersistentSessionManager.instance) {
      PersistentSessionManager.instance = new PersistentSessionManager();
    }
    return PersistentSessionManager.instance;
  }

  private initializeSession(): void {
    // Check if user is already logged in
    const token = this.getStoredToken();
    const user = this.getStoredUser();

    if (token && user) {
      console.log('Persistent session found, user remains logged in');
      this.startTokenRefresh();
    }
  }

  public login(token: string, user: StoredUser, refreshToken?: string): void {
    // Store authentication data in localStorage (persistent)
    localStorage.setItem(this.TOKEN_KEY, token);
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));

    if (refreshToken) {
      localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
    }

    // Start token refresh timer
    this.startTokenRefresh();

    console.log('User logged in with persistent session');
  }

  public logout(): void {
    // Clear all stored data
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);

    // Stop token refresh
    this.stopTokenRefresh();

    console.log('User logged out, persistent session cleared');
  }

  public getStoredToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  public getStoredUser(): StoredUser | null {
    const userStr = localStorage.getItem(this.USER_KEY);
    if (userStr) {
      try {
        return JSON.parse(userStr) as StoredUser;
      } catch (error) {
        console.error('Error parsing stored user:', error);
        return null;
      }
    }
    return null;
  }

  public getStoredRefreshToken(): string | null {
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  public isLoggedIn(): boolean {
    const token = this.getStoredToken();
    const user = this.getStoredUser();
    return !!(token && user);
  }

  private startTokenRefresh(): void {
    // Clear existing timer
    this.stopTokenRefresh();

    // Set up new timer for token refresh
    this.refreshTimer = setInterval(() => {
      this.refreshTokenIfNeeded();
    }, this.REFRESH_INTERVAL);

    console.log('Token refresh timer started');
  }

  private stopTokenRefresh(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
      console.log('Token refresh timer stopped');
    }
  }

  private async refreshTokenIfNeeded(): Promise<void> {
    try {
      const refreshToken = this.getStoredRefreshToken();
      if (!refreshToken) {
        console.log('No refresh token available');
        return;
      }

      // Attempt to refresh the token
      const response = await fetch('/api/auth/refresh/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          refresh: refreshToken,
        }),
      });

      if (response.ok) {
        const data = await response.json();

        // Update stored token
        localStorage.setItem(this.TOKEN_KEY, data.access);

        if (data.refresh) {
          localStorage.setItem(this.REFRESH_TOKEN_KEY, data.refresh);
        }

        console.log('Token refreshed successfully');
      } else {
        console.log('Token refresh failed, user will need to log in again');
        // Don't automatically logout - let user decide when to log in again
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      // Don't automatically logout on network errors
    }
  }

  public async validateToken(): Promise<boolean> {
    try {
      const token = this.getStoredToken();
      if (!token) {
        return false;
      }

      const response = await fetch('/api/auth/profile/', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      return response.ok;
    } catch (error) {
      console.error('Error validating token:', error);
      return false;
    }
  }

  public updateUserData(userData: StoredUser): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(userData));
    console.log('User data updated in persistent session');
  }

  public getSessionInfo(): {
    isLoggedIn: boolean;
    user: StoredUser | null;
    token: string | null;
    hasRefreshToken: boolean;
  } {
    return {
      isLoggedIn: this.isLoggedIn(),
      user: this.getStoredUser(),
      token: this.getStoredToken(),
      hasRefreshToken: !!this.getStoredRefreshToken(),
    };
  }

  // Handle browser close/refresh
  public handleBeforeUnload(): void {
    // Don't clear session data on page unload
    // This maintains the mobile app behavior
    console.log('Page unloading, maintaining persistent session');
  }

  // Handle visibility change (tab switching)
  public handleVisibilityChange(): void {
    if (document.hidden) {
      console.log('Tab hidden, maintaining session');
    } else {
      console.log('Tab visible, checking session validity');
      this.validateToken();
    }
  }
}

// Export singleton instance
export const persistentSessionManager = PersistentSessionManager.getInstance();

// Initialize event listeners for browser events
if (typeof window !== 'undefined') {
  // Handle page unload
  window.addEventListener('beforeunload', () => {
    persistentSessionManager.handleBeforeUnload();
  });

  // Handle visibility change
  document.addEventListener('visibilitychange', () => {
    persistentSessionManager.handleVisibilityChange();
  });

  // Handle focus/blur events
  window.addEventListener('focus', () => {
    persistentSessionManager.handleVisibilityChange();
  });
}
